import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Card, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { getSessions, getSurvey } from '../services/api';

const SessionList = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [surveyData, sessionsData] = await Promise.all([
          getSurvey(surveyId),
          getSessions(surveyId)
        ]);
        
        setSurvey(surveyData);
        setSessions(sessionsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError('Failed to load sessions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [surveyId]);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (!survey) {
    return (
      <Alert variant="warning">
        Survey not found. It may have been deleted or the ID is incorrect.
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Sessions</h1>
          <h5 className="text-muted">
            <Link to={`/surveys/${surveyId}`}>{survey.title}</Link>
          </h5>
        </div>
        <Button as={Link} to={`/surveys/${surveyId}/sessions/create`} variant="primary">
          Start New Session
        </Button>
      </div>
      
      <Card>
        <Card.Body>
          {sessions.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-4">No sessions found for this survey</p>
              <Button as={Link} to={`/surveys/${surveyId}/sessions/create`} variant="primary">
                Create Your First Session
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Session Name</th>
                  <th>Created</th>
                  <th>Attachments</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => (
                  <tr key={session.id}>
                    <td>
                      <Link to={`/sessions/${session.id}`}>
                        {session.name || `Session ${session.id.slice(0, 8)}`}
                      </Link>
                    </td>
                    <td>{new Date(session.created_at).toLocaleString()}</td>
                    <td>
                      <Badge bg="info">
                        {session.attachment_count || 0} attachments
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        as={Link} 
                        to={`/sessions/${session.id}`} 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                      >
                        View
                      </Button>
                      <Button 
                        as={Link} 
                        to={`/sessions/${session.id}/realtime`} 
                        variant="outline-success" 
                        size="sm"
                      >
                        Realtime
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SessionList;